import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { IconButton, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { EmptyState } from "../components/EmptyState";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageContent } from "../components/PageContent";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../components/Table";
import { CatalogListName } from "../components/CatalogOriginChip";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { useShowCatalogOriginMarkers } from "../hooks/useShowCatalogOriginMarkers";
import { CATALOG_NEW_ITEM_ID } from "./catalog/catalogNav";
import {
  useGetSuppliersQuery,
  useDeleteSupplierMutation,
} from "../query/suppliersQuery";
import type { SupplierId } from "@KassaroAB/shared/types/ids";

export const SuppliersView = () => {
  const { t } = useTranslation();
  const { scope, pagePath } = useCatalogScope("suppliers");
  const showCatalogOrigin = useShowCatalogOriginMarkers(scope);

  const [deleteId, setDeleteId] = useState<SupplierId | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: suppliers,
    isLoading,
    error: loadError,
  } = useGetSuppliersQuery(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteSupplierMutation(scope);

  const supplierList = suppliers ?? [];
  const deleteName =
    supplierList.find((supplier) => supplier.id === deleteId)?.name ?? "";

  const confirmDelete = () => {
    if (!deleteId) return;
    deleteMutation(deleteId, {
      onSuccess: () => {
        setSuccessMessage(t("suppliersView.successDeleted"));
        setDeleteId(null);
      },
      onError: () => setSuccessMessage(null),
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  if (isLoading && supplierList.length === 0) {
    return (
      <PageContent title={t("suppliersView.title")}>
        <LoadingContainer message={t("suppliersView.loading")} />
      </PageContent>
    );
  }

  return (
    <PageContent
      title={t("suppliersView.title")}
      actions={
        <RouterButtonLink to={`${pagePath}/${CATALOG_NEW_ITEM_ID}`}>
          <Plus size={16} aria-hidden />
          {t("suppliersView.create")}
        </RouterButtonLink>
      }
    >
      {loadError && (
        <InfoBox variant="error">{t("suppliersView.errorLoad")}</InfoBox>
      )}
      {deleteError && (
        <InfoBox variant="error">{t("suppliersView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      {supplierList.length > 0 ? (
        <Card>
          <Table minWidth="640px">
            <TableHeader>
              <tr>
                <TableHeaderCell>
                  {t("suppliersView.tableName")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("suppliersView.tableContact")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("suppliersView.tableCity")}
                </TableHeaderCell>
                <TableHeaderCell align="right">{null}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {supplierList.map((supplier) => (
                <TableRow key={supplier.id} to={`${pagePath}/${supplier.id}`}>
                  <TableCell noWrap>
                    <CatalogListName
                      name={supplier.name}
                      origin={supplier.origin}
                      showOrigin={showCatalogOrigin}
                    />
                  </TableCell>
                  <TableCell noWrap>{supplier.contactPerson ?? ""}</TableCell>
                  <TableCell noWrap>{supplier.city ?? ""}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      type="button"
                      $color="danger"
                      aria-label={t("common.delete")}
                      disabled={isDeletePending}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setDeleteId(supplier.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <EmptyState
            message={`${t("suppliersView.empty")} ${t("suppliersView.emptyHint")}`}
          />
        </Card>
      )}
      <ConfirmDialog
        isOpen={deleteId != null}
        title={t("supplierForm.deleteSupplier")}
        message={t("suppliersView.deleteConfirm", { name: deleteName })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </PageContent>
  );
};
